import { useState } from 'react';
import { Share2, Twitter, Linkedin, Facebook, Link as LinkIcon, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';

interface SocialShareButtonsProps {
  /** The URL to share */
  url: string;
  /** The title of the content being shared */
  title: string;
  /** A brief description for social media sharing */
  description?: string;
  /** The author of the content */
  author?: string;
  /** CSS class name for styling */
  className?: string;
  /** Variant for different display styles */
  variant?: 'horizontal' | 'vertical' | 'compact';
  /** Source context for analytics tracking */
  trackingSource?: string;
}

/**
 * SocialShareButtons component for sharing blog posts and content
 * 
 * Features:
 * - Twitter/X, LinkedIn, Facebook sharing
 * - Copy link functionality with feedback
 * - Analytics tracking for each share method
 * - Responsive design with multiple layouts
 * - Accessibility support
 */
export function SocialShareButtons({
  url,
  title,
  description = '',
  author = '',
  className = '',
  variant = 'horizontal',
  trackingSource = 'social_share'
}: SocialShareButtonsProps) {
  const [copySuccess, setCopySuccess] = useState(false);
  const { trackEvent } = useAnalytics();

  // Prepare sharing content
  const shareTitle = title;
  const shareDescription = description || `Check out this article: ${title}`;
  const shareUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  
  // Add author attribution if available
  const shareText = author ? `${shareTitle} by ${author}` : shareTitle;

  /**
   * Handle copying the URL to clipboard
   */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      
      // Track copy link event
      trackEvent('social_share_copy_link', {
        url: shareUrl,
        title: shareTitle,
        source: trackingSource
      });

      // Reset success state after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  /**
   * Handle Twitter/X sharing
   */
  const handleTwitterShare = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    
    trackEvent('social_share_twitter', {
      url: shareUrl,
      title: shareTitle,
      source: trackingSource
    });

    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  /**
   * Handle LinkedIn sharing
   */
  const handleLinkedInShare = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    
    trackEvent('social_share_linkedin', {
      url: shareUrl,
      title: shareTitle,
      source: trackingSource
    });

    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  /**
   * Handle Facebook sharing
   */
  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    
    trackEvent('social_share_facebook', {
      url: shareUrl,
      title: shareTitle,
      source: trackingSource
    });

    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
  };

  // Determine layout classes based on variant
  const getLayoutClasses = () => {
    switch (variant) {
      case 'vertical':
        return 'flex flex-col gap-2';
      case 'compact':
        return 'flex gap-1';
      default:
        return 'flex flex-wrap gap-3';
    }
  };

  const getButtonSize = () => {
    return variant === 'compact' ? 'sm' : 'default';
  };

  const shouldShowLabels = variant !== 'compact';

  return (
    <div className={cn('social-share-buttons', getLayoutClasses(), className)}>
      {/* Share label for non-compact variants */}
      {shouldShowLabels && variant === 'horizontal' && (
        <div className="flex items-center text-sm text-muted-foreground mr-2">
          <Share2 className="mr-2 h-4 w-4" />
          <span>Share:</span>
        </div>
      )}

      {/* Twitter/X Share Button */}
      <Button
        variant="outline"
        size={getButtonSize()}
        onClick={handleTwitterShare}
        className="hover:bg-blue-50 hover:border-blue-200 transition-colors"
        aria-label={`Share "${title}" on Twitter`}
      >
        <Twitter className="h-4 w-4" />
        {shouldShowLabels && <span className="ml-2">Twitter</span>}
      </Button>

      {/* LinkedIn Share Button */}
      <Button
        variant="outline"
        size={getButtonSize()}
        onClick={handleLinkedInShare}
        className="hover:bg-blue-50 hover:border-blue-200 transition-colors"
        aria-label={`Share "${title}" on LinkedIn`}
      >
        <Linkedin className="h-4 w-4" />
        {shouldShowLabels && <span className="ml-2">LinkedIn</span>}
      </Button>

      {/* Facebook Share Button */}
      <Button
        variant="outline"
        size={getButtonSize()}
        onClick={handleFacebookShare}
        className="hover:bg-blue-50 hover:border-blue-200 transition-colors"
        aria-label={`Share "${title}" on Facebook`}
      >
        <Facebook className="h-4 w-4" />
        {shouldShowLabels && <span className="ml-2">Facebook</span>}
      </Button>

      {/* Copy Link Button */}
      <Button
        variant="outline"
        size={getButtonSize()}
        onClick={handleCopyLink}
        className={cn(
          "transition-colors",
          copySuccess 
            ? "bg-green-50 border-green-200 text-green-700" 
            : "hover:bg-gray-50 hover:border-gray-200"
        )}
        aria-label={copySuccess ? "Link copied!" : "Copy link"}
      >
        {copySuccess ? (
          <Check className="h-4 w-4" />
        ) : (
          <LinkIcon className="h-4 w-4" />
        )}
        {shouldShowLabels && (
          <span className="ml-2">
            {copySuccess ? 'Copied!' : 'Copy Link'}
          </span>
        )}
      </Button>
    </div>
  );
} 